import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PowerSignatureProgress from '../poderes/PowerSignatureProgress';
import './OrderActivity.css';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const labels = { user: 'Cliente', admin: 'Administrador Wisby', abogado_wisby: 'Abogado Wisby', notario: 'Notario titular', admin_notaria: 'Administrador de notaría', abogado_notaria: 'Abogado de notaría' };
const stageLabels = { wisby_acta: 'Elaboración del acta por Wisby', proyecto_ep: 'Elaboración de escritura pública', pendiente_aprobacion_notario: 'Pendiente de visto bueno del notario', proyecto_aprobado: 'Proyecto aprobado', pendiente_registro_publico: 'Pendiente de Registro Público', en_registro: 'En Registro Público', documento_digitalizado: 'Escritura digitalizada disponible', en_apostilla: 'En legalización o apostilla', apostilla_finalizada: 'Legalización concluida', entregado: 'Primer testimonio y copias entregados', pendiente_revision_interna: 'Pendiente de revisión por Wisby', esperando_firma_admin: 'Pendiente de firma del escrutador', esperando_firma_cliente: 'En firmas', borrador: 'Borrador' };
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const orderDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Fecha no disponible'
    : new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};
const orderEventText = item => item?.details || item?.action || 'Actualización de la orden';
const orderStatus = item => stageLabels[item?.stage || item?.status] || 'En trámite';
async function request(path, options = {}) {
  const response = await fetch(`${API}/api/${path}`, { ...options, headers: { 'Content-Type': 'application/json', 'x-auth-token': token(), ...options.headers } });
  const data = await response.json(); if (!response.ok) throw new Error(data.msg || 'No se pudo completar la operación.'); return data;
}
function ActivityIcon({ name, size = 18 }) {
  const paths = {
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    check: <><path d="m3 12 4 4L17 6" /><path d="m12 16 9-10" /></>,
    close: <><path d="m6 6 12 12M6 18 18 6" /></>,
    document: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    arrow: <><path d="M5 12h14m-6-6 6 6-6 6" /></>,
    file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6" /></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}
export default function OrderActivity({ currentUser, userRole, onLogin, inHeader = false }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [roles, setRoles] = useState([]); const [open, setOpen] = useState(false); const [order, setOrder] = useState(null); const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false); const accessStarted = useRef(false); const activityRef = useRef(null);
  const load = useCallback(async () => { try { setData(await request('notifications')); } catch (e) { setError(e.message); } }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search); const access = params.get('orderAccess');
    if (!access || accessStarted.current) return;
    accessStarted.current = true; setBusy(true);
    // Remove the credential before any navigation or third-party request.
    params.delete('orderAccess'); window.history.replaceState({}, '', `${window.location.pathname}${params.size ? `?${params}` : ''}`);
    request('notifications/access', { method: 'POST', body: JSON.stringify({ token: access }) }).then(data => {
      onLogin([data.user.name, data.user.lastName].filter(Boolean).join(' '), data.token, data.user.email, data.user.role, false);
      window.history.replaceState({}, '', `?order=${encodeURIComponent(data.orderId)}`);
      return request(`notifications/orders/${data.orderId}`);
    }).then(data => setOrder(data.order)).catch(e => setError(e.message)).finally(() => setBusy(false));
  }, [onLogin]);
  useEffect(() => {
    if (!currentUser) { setOrder(null); setShowOrderDetails(false); setOpen(false); setData({ items: [], unread: 0 }); setRoles([]); return; }
    load(); request('roles/me').then(data => setRoles(data.roles)).catch(e => setError(e.message));
    const params = new URLSearchParams(window.location.search);
    const id = params.get('order');
    // Un enlace directo prepara la orden para consulta, pero no abre un diálogo
    // inesperado al entrar a Wisby. En workspace, la pantalla se encarga del folio.
    if (id && !params.has('workspace') && /^[a-f\d]{24}$/i.test(id)) {
      request(`notifications/orders/${id}`)
        .then(data => setOrder(data.order))
        .catch(e => setError(e.message));
    }
    const timer = setInterval(load, 30000); return () => clearInterval(timer);
  }, [currentUser, load]);
  useEffect(() => {
    if (!open) return;
    const dismiss = event => { if (!activityRef.current?.contains(event.target)) setOpen(false); };
    const escape = event => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', dismiss); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', dismiss); document.removeEventListener('keydown', escape); };
  }, [open]);
  const view = async notice => {
    setBusy(true); setError('');
    try { const result = await request(`notifications/orders/${notice.poderId}`); setOrder(result.order); setShowOrderDetails(true); await request(`notifications/${notice._id}/read`, { method: 'PATCH' }); await load(); setOpen(false); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const closeOrderDetails = useCallback(() => {
    setShowOrderDetails(false);
    setOrder(null);
    // Limpia el enlace de consulta para que al regresar no vuelva a aparecer.
    const params = new URLSearchParams(window.location.search);
    if (params.has('order') && !params.has('workspace')) {
      params.delete('order');
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${params.size ? `?${params}` : ''}`);
    }
  }, []);
  useEffect(() => {
    if (!showOrderDetails) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = event => {
      if (event.key === 'Escape') closeOrderDetails();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showOrderDetails, closeOrderDetails]);
  const switchRole = async role => {
    setBusy(true); setError('');
    try { await request('roles/active', { method: 'POST', body: JSON.stringify({ role }) }); const storage = localStorage.getItem('token') ? localStorage : sessionStorage; storage.setItem('userRole', role); window.location.reload(); } catch (e) { setError(e.message); setBusy(false); }
  };
  const downloadDeed = async () => {
    setBusy(true); setError('');
    try { const response = await fetch(`${API}/api/poderes/${order._id}/notarial-documents/digitizedDeed`, { headers: { 'x-auth-token': token() } }); if (!response.ok) { const data = await response.json(); throw new Error(data.msg || 'No se pudo descargar la escritura.'); } const url = URL.createObjectURL(await response.blob()); const link = document.createElement('a'); link.href = url; const mime = response.headers?.get('Content-Type') || ''; const extension = mime.includes('png') ? 'png' : mime.includes('jpeg') ? 'jpg' : 'pdf'; link.download = `escritura-${order.folio || order._id}.${extension}`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const workspace = async () => {
    setBusy(true); setError('');
    try { if (order.workRole && order.workRole !== userRole) { await request('roles/active', { method: 'POST', body: JSON.stringify({ role: order.workRole }) }); const storage = localStorage.getItem('token') ? localStorage : sessionStorage; storage.setItem('userRole', order.workRole); } window.location.assign(`/?order=${encodeURIComponent(order._id)}&workspace=1`); } catch (e) { setError(e.message); setBusy(false); }
  };
  const roleSelector = roles.length > 1 && <label>Trabajar como <select aria-label="Rol activo" disabled={busy} value={userRole} onChange={e => switchRole(e.target.value)}>{roles.map(role => <option key={role} value={role}>{labels[role]}</option>)}</select></label>;
  return <div ref={activityRef} className={`order-activity${inHeader ? ' order-activity--header' : ''}`}>
    {currentUser && <div className="oa-toolbar">{!inHeader && roleSelector}
      <button type="button" aria-label={`Notificaciones: ${data.unread} sin leer`} aria-expanded={open} onClick={() => { setOpen(!open); load(); }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 21h6" /></svg> {data.unread > 0 && <strong>{data.unread}</strong>}</button>
    </div>}
    {(busy || error) && <div className="oa-feedback">{busy && <p role="status">Cargando…</p>}{error && <p role="alert">{error}<button onClick={() => setError('')} aria-label="Cerrar aviso">×</button></p>}</div>}
    {open && <section className="oa-inbox" aria-label="Notificaciones">
      <header className="oa-inbox-heading">
        <div className="oa-inbox-title"><h2>Notificaciones</h2>{data.unread > 0 && <span className="oa-inbox-count">{data.unread}</span>}</div>
        <div className="oa-inbox-actions">
          <button className="oa-mark-read" type="button" aria-label="Marcar todas como leídas" title="Marcar todas como leídas" disabled={!data.unread || busy} onClick={async () => { try { await request('notifications/read', { method: 'PATCH' }); await load(); } catch (e) { setError(e.message); } }}><ActivityIcon name="check" size={15} /><span>Leer todas</span></button>
          <button className="oa-close-inbox" type="button" aria-label="Cerrar notificaciones" onClick={() => setOpen(false)}><ActivityIcon name="close" size={15} /></button>
        </div>
      </header>
      {inHeader && roleSelector}
      <div className="oa-inbox-scroll" role="region" aria-label="Lista de notificaciones" tabIndex={0}>
      {!data.items.length && <div className="oa-empty-state"><span className="oa-empty-icon"><ActivityIcon name="bell" size={22} /></span><p>No tienes notificaciones.</p><span>Te avisaremos cuando haya novedades.</span></div>}
      <ul>{data.items.map(notice => <li key={notice._id} className={notice.readAt ? '' : 'oa-unread'}>
        <button disabled={busy} onClick={() => view(notice)}><span className="oa-notice-icon"><ActivityIcon name="document" size={17} /></span><span className="oa-notice-copy"><strong>{notice.title}</strong><span>{notice.message}</span><time>{new Date(notice.createdAt).toLocaleString('es-MX')}</time></span>{!notice.readAt && <span className="oa-unread-dot" aria-label="Sin leer" />}</button>
      </li>)}</ul>
      </div>
    </section>}

    {order && !showOrderDetails && <aside className="oa-order-ready" role="status" aria-label="Orden disponible">
      <span className="oa-order-ready-icon"><ActivityIcon name="file" size={18} /></span>
      <div className="oa-order-ready-text"><strong>Orden {order.folio || 'disponible'}</strong><span>Consulta el avance cuando lo necesites.</span></div>
      <button type="button" className="oa-order-ready-open" onClick={() => setShowOrderDetails(true)}>Ver detalles <ActivityIcon name="arrow" size={15} /></button>
      <button type="button" className="oa-order-ready-dismiss" aria-label="Ocultar aviso de orden" onClick={closeOrderDetails}><ActivityIcon name="close" size={16} /></button>
    </aside>}

    {order && showOrderDetails && createPortal(
      <div className="oa-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeOrderDetails(); }}>
        <section className="oa-order" role="dialog" aria-modal="true" aria-label="Orden de servicio">
          <header className="oa-order-header">
            <div className="oa-order-heading-copy">
              <span className="oa-order-eyebrow"><ActivityIcon name="file" size={15} /> SEGUIMIENTO DE SERVICIO</span>
              <h2 id="oa-order-heading">Orden de servicio <span>{order.folio || 'Sin folio'}</span></h2>
              <p>Consulta el avance de tu solicitud, el proceso de firmas y las actualizaciones.</p>
            </div>
            <button type="button" className="oa-order-dismiss" autoFocus aria-label="Cerrar orden" onClick={closeOrderDetails}>
              <ActivityIcon name="close" size={21} />
            </button>
          </header>

          <div className="oa-order-scroll">
            {error && <div className="oa-order-inline-error" role="alert">{error}</div>}
            <section className="oa-order-summary" aria-label="Resumen de la orden">
              <div className="oa-order-summary-icon"><ActivityIcon name="document" size={23} /></div>
              <div className="oa-order-summary-main">
                <span className="oa-order-summary-label">SOLICITANTE</span>
                <strong>{order.companyName || 'Nombre pendiente'}</strong>
              </div>
              <div className="oa-order-status">
                <span className="oa-order-summary-label">ESTADO ACTUAL</span>
                <strong><span className="oa-order-status-dot" /> {orderStatus(order)}</strong>
              </div>
            </section>

            {(['admin', 'abogado_wisby', 'notario', 'admin_notaria', 'abogado_notaria'].includes(order.workRole || userRole)
              || order.canSign || (order.digitizedAvailable && userRole === 'user')) && (
              <section className="oa-order-actions" aria-label="Acciones de la orden">
                {['admin', 'abogado_wisby', 'notario', 'admin_notaria', 'abogado_notaria'].includes(order.workRole || userRole) &&
                  <button className="oa-order-primary" type="button" disabled={busy} onClick={workspace}>
                    <ActivityIcon name="file" size={17} /> Abrir expediente de trabajo <ActivityIcon name="arrow" size={16} />
                  </button>}
                {order.canSign &&
                  <button className="oa-order-secondary" type="button" disabled={busy} onClick={async () => {
                    setBusy(true); setError('');
                    try {
                      const result = await request(`notifications/orders/${order._id}/sign`, { method: 'POST' });
                      window.location.assign(result.url);
                    } catch (e) { setError(e.message); } finally { setBusy(false); }
                  }}>Firmar acta con e.firma</button>}
                {order.digitizedAvailable && userRole === 'user' &&
                  <button className="oa-order-secondary" type="button" disabled={busy} onClick={downloadDeed}>Descargar escritura digitalizada</button>}
              </section>
            )}

            <section className="oa-order-section oa-order-signatures" aria-labelledby="oa-order-signatures-title">
              <div className="oa-order-section-head">
                <span className="oa-order-section-icon"><ActivityIcon name="check" size={19} /></span>
                <div><h3 id="oa-order-signatures-title">Proceso de firmas</h3><p>Consulta los participantes y el orden de firma.</p></div>
              </div>
              <PowerSignatureProgress poder={order} />
            </section>

            <section className="oa-order-section oa-order-history" aria-labelledby="oa-order-history-title">
              <div className="oa-order-section-head">
                <span className="oa-order-section-icon"><ActivityIcon name="clock" size={19} /></span>
                <div><h3 id="oa-order-history-title">Historial de la orden</h3><p>Registro cronológico de los avances de tu trámite.</p></div>
                <span className="oa-order-history-count">{order.history?.length || 0} eventos</span>
              </div>
              {order.history?.length ? (
                <ol className="oa-order-timeline">
                  {order.history.map((item, index) =>
                    <li className="oa-order-event" key={item._id || `${item.performedAt}-${index}`}>
                      <span className="oa-order-event-marker" aria-hidden="true"><ActivityIcon name="check" size={14} /></span>
                      <div className="oa-order-event-content">
                        <p>{orderEventText(item)}</p>
                        <time dateTime={item.performedAt || undefined}><ActivityIcon name="clock" size={13} />{orderDate(item.performedAt)}</time>
                      </div>
                    </li>)}
                </ol>
              ) : (
                <p className="oa-order-history-empty">Todavía no hay eventos registrados para esta orden.</p>
              )}
            </section>
          </div>
          <footer className="oa-order-footer">
            <span>Información de seguimiento de Wisby</span>
            <button type="button" onClick={closeOrderDetails}>Cerrar detalles</button>
          </footer>
        </section>
      </div>, document.body)}

  </div>;
}
